import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function canUse(user, module) {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (!user.modules) return true; // unrestricted (back-compat)
  return user.modules.includes(module);
}

export function defaultRouteFor(user) {
  if (!user) return "/login";
  if (canUse(user, "website_generator")) return "/websites";
  if (canUse(user, "image_generator")) return "/images";
  if (canUse(user, "document_editor")) return "/documents";
  return "/login";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("token");
      if (!token) { setUser(false); setReady(true); return; }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem("token");
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{
      user, ready, login, logout,
      isAdmin: user?.role === "admin",
      hasModule: (m) => canUse(user, m),
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
